import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    User, Mail, Phone, MapPin, Hash, Calendar,
    ShieldBan, ShieldCheck, Landmark, ShoppingBag, Eye
} from 'lucide-react';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { StatusBadge } from '@/components/common/StatusBadge';
import { SectionCard } from '@/components/common/SectionCard';
import { DetailItem } from '@/components/common/DetailItem';
import { NoDataFound } from '@/components/common/NoDataFound';
import Button from '@/components/common/Button';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { formatDateTime, showToastNotification, GetFilterUrl, formatRand } from '@/utility/helper';
import { CustomerData, CustomerDetailResponse } from '@/interfaces/Customer/customer.interface';
import { IOrder, IOrderResponse } from '@/interfaces/Order/order.interface';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import Pagination from '@/components/common/Pagination';
import { RoutesLink } from '@/utility/RouteLinks';
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';
import { useFilter } from '@/hooks/useFilter';
import { IFilter } from '@/interfaces/common.interface';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';

export const CustomerDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useRoleNavigate();
    const backButton = useNavigate();
    const { canBlock } = usePermissions();
    const [customer, setCustomer] = useState<CustomerData | null>(null);
    const [loading, setLoading] = useState(true);
    const [blockLoading, setBlockLoading] = useState(false);
    const [showBlockConfirm, setShowBlockConfirm] = useState(false);
    const [orders, setOrders] = useState<IOrder[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersPageInfo, setOrdersPageInfo] = useState({ limit: 5, page: 1, total: 0, totalPages: 0 });
    const { filters, handlePageChange, debouncedFilters } = useFilter<IFilter>({
        page: 1,
        limit: 10,
        userId: id
    });

    useEffect(() => {
        if (id) {
            fetchCustomer();
        }
    }, [id]);

    useEffect(() => {
        fetchCustomerOrders();
    }, [debouncedFilters.page, debouncedFilters.limit]);

    const fetchCustomer = async () => {
        setLoading(true);
        try {
            const response = await callApi<CustomerDetailResponse>(
                `${API_URL}${API_ENDPOINT.user}/detail/${id}`,
                { method: 'GET' }
            );
            setCustomer(response?.data);
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomerOrders = async () => {
        setOrdersLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            const response = await callApi<IOrderResponse>(
                `${API_URL}${API_ENDPOINT.orders}?${queryParams}`,
                { method: 'GET' }
            );
            if (response?.data) {
                setOrders(response?.data?.order || []);
                setOrdersPageInfo(response?.data?.pageInfo || { limit: 5, page: 1, total: 0, totalPages: 0 });
            }
        } catch (error) {
            console.error("Failed to fetch orders", error);
        } finally {
            setOrdersLoading(false);
        }
    };



    const orderColumns: TableColumn<IOrder>[] = [
        {
            key: 'orderId',
            header: 'Order',
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-bold text-primary">#{item.orderId}</span>
                    <span className="text-[10px] text-muted-foreground">{formatDateTime(item.createdAt)}</span>
                </div>
            ),
        },
        {
            key: 'paymentMethod',
            header: 'Payment',
            render: (item) => (
                <div className="flex flex-col">
                    <span className="text-sm font-semibold text-foreground">{item.paymentMethod || '---'}</span>
                    <span className={`text-[10px] font-semibold capitalize ${item.paymentStatus === 'paid' ? 'text-green-600' : 'text-amber-600'}`}>
                        {item.paymentStatus}
                    </span>
                </div>
            )
        },
        {
            key: 'finalAmount',
            header: 'Total',
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-bold text-foreground">{formatRand(item.finalAmount)}</span>
                </div>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (item) => <StatusBadge status={item.status} />,
        },
    ];

    const handleBlockToggle = async () => {
        if (!customer) return;
        setBlockLoading(true);
        const newBlockedState = !customer.isBlocked;
        try {
            await callApi(
                `${API_URL}${API_ENDPOINT.user}/block/${id}?isBlocked=${newBlockedState}`,
                { method: 'PATCH' }
            );
            showToastNotification(
                'success',
                `Customer has been ${newBlockedState ? 'blocked' : 'unblocked'} successfully.`
            );
            setCustomer(prev => prev ? { ...prev, isBlocked: newBlockedState } : null);
        } catch (error) {
            ThrowError(error);
        } finally {
            setBlockLoading(false);
            setShowBlockConfirm(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </Layout>
        );
    }

    if (!customer) {
        return (
            <Layout>
                <NoDataFound
                    title="Customer Not Found"
                    description="The customer you are looking for does not exist or may have been removed."
                    onAction={() => backButton(-1)}
                />
            </Layout>
        );
    }

    const isBlocked = customer.isBlocked;

    return (
        <Layout>
            <div className="space-y-8 pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">

                {/* Header */}
                <MainHeading
                    title="Customer Details"
                    description={
                        <div className="flex items-center gap-2 text-muted-foreground/80 mt-1">
                            <Calendar className="w-3.5 h-3.5" />
                            <span className="text-sm">Registered {formatDateTime(customer.createdAt)}</span>
                        </div>
                    }
                    showBack
                    onBack={() => backButton(-1)}
                />

                {/* Status Banner */}
                {canBlock(RESOURCES.CUSTOMER) && <div>
                    {isBlocked ? (
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-red-50 border border-red-200 rounded-2xl px-6 py-4">
                            <div className="flex items-center gap-3">
                                <ShieldBan className="w-5 h-5 text-red-600 shrink-0" />
                                <div>
                                    <p className="text-sm font-bold text-red-800">Customer Blocked</p>
                                    <p className="text-xs text-red-600 mt-0.5">This customer is currently blocked and cannot access the platform.</p>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50 gap-2 shrink-0"
                                onClick={() => setShowBlockConfirm(true)}
                                leftIcon={<ShieldCheck className="w-4 h-4" />}
                            >
                                Unblock Customer
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-2xl px-6 py-4">
                            <div className="flex items-center gap-3">
                                <ShieldCheck className="w-5 h-5 text-green-600 shrink-0" />
                                <div>
                                    <p className="text-sm font-bold text-green-800">Customer Active</p>
                                    <p className="text-xs text-green-600 mt-0.5">This customer has full access to the platform.</p>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                className="border-red-300 text-red-700 hover:bg-red-50 gap-2 shrink-0"
                                onClick={() => setShowBlockConfirm(true)}
                                leftIcon={<ShieldBan className="w-4 h-4" />}
                            >
                                Block Customer
                            </Button>
                        </div>
                    )}
                </div>}

                {/* Main Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

                    {/* Left — 2 columns */}
                    <div className="lg:col-span-2 space-y-8">

                        {/* Personal Info */}
                        <SectionCard title="Personal Information" icon={<User />}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-1">
                                <DetailItem
                                    label="Full Name"
                                    value={customer.fullName}
                                    icon={<User className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                />
                                <DetailItem
                                    label="Email Address"
                                    value={customer.email}
                                    icon={<Mail className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                />
                                <DetailItem
                                    label="Phone Number"
                                    value={customer.phone || '—'}
                                    icon={<Phone className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                />
                                <DetailItem
                                    label="Date of Birth"
                                    value={customer.dob ? formatDateTime(customer.dob, "YYMMDD") : '—'}
                                    icon={<Calendar className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                />
                                <DetailItem
                                    label="Gender"
                                    value={customer.gender || '—'}
                                    icon={<User className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                />
                            </div>
                        </SectionCard>

                        {/* Addresses */}
                        {/* {customer.addresses && customer.addresses.length > 0 && (
                            <SectionCard title="Saved Addresses" icon={<MapPin />}>
                                <div className="space-y-6">
                                    {customer.addresses.map((addr) => (
                                        <div key={addr.id} className="rounded-2xl border border-border/50 bg-secondary/5 p-6 space-y-4">
                                            <div className="flex items-center gap-3 pb-2 border-b border-border/40">
                                                <div className="p-2 bg-primary/10 rounded-xl">
                                                    <MapPin className="w-4 h-4 text-primary" />
                                                </div>
                                                <h4 className="font-bold text-foreground capitalize">
                                                    {addr.label || addr.addressType}
                                                </h4>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {addr.fullName && (
                                                    <DetailItem label="Full Name" value={addr.fullName}
                                                        icon={<User className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.phoneNumber && (
                                                    <DetailItem label="Phone" value={addr.phoneNumber}
                                                        icon={<Phone className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.houseNumber && (
                                                    <DetailItem label="House Number" value={addr.houseNumber}
                                                        icon={<Hash className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.streetName && (
                                                    <DetailItem label="Street Name" value={addr.streetName}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.addressLine && (
                                                    <DetailItem label="Address Line" value={addr.addressLine}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all sm:col-span-2" />
                                                )}
                                                {addr.suburb && (
                                                    <DetailItem label="Suburb" value={addr.suburb}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.city && (
                                                    <DetailItem label="City" value={addr.city}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.province && (
                                                    <DetailItem label="Province" value={addr.province}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.postalCode && (
                                                    <DetailItem label="Postal Code" value={addr.postalCode}
                                                        icon={<Hash className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.country && (
                                                    <DetailItem label="Country" value={addr.country}
                                                        icon={<Landmark className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.deliveryInstructions && (
                                                    <DetailItem label="Delivery Instructions" value={addr.deliveryInstructions}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all sm:col-span-2" />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </SectionCard>
                        )} */}

                    </div>


                    {/* Right Sidebar */}
                    <div className="space-y-8 sticky top-6">
                        <SectionCard title="Account Status" icon={<ShieldCheck />}>
                            <div className="grid grid-cols-1 gap-y-6">
                                <DetailItem
                                    label="Account Status"
                                    value={<StatusBadge status={customer.isBlocked ? "blocked" : "active"} />}
                                    icon={<ShieldCheck className="w-4 h-4" />}
                                />
                                <DetailItem
                                    label="Phone Verified"
                                    value={<StatusBadge status={customer.isPhoneVerified ? "verified" : "not_Verified"} />}
                                    icon={<Phone className="w-4 h-4" />}
                                />
                                <div className="pt-2 border-t border-border/40">
                                    <DetailItem
                                        label="Registered On"
                                        value={formatDateTime(customer.createdAt)}
                                        icon={<Calendar className="w-4 h-4" />}
                                    />
                                </div>
                            </div>
                        </SectionCard>
                    </div>
                </div>
                {/* Order History */}
                <SectionCard title="Recent Orders" icon={<ShoppingBag />}>
                    <div className="space-y-4">
                        <CommonTable
                            columns={orderColumns}
                            data={orders}
                            loading={ordersLoading}
                            actions={[
                                {
                                    text: 'View Details',
                                    icon: <Eye className="w-4 h-4 cursor-pointer" />,
                                    onClick: (row) => navigate(RoutesLink.ViewOrders.replace(':id', row.id)),
                                }
                            ]}
                            emptyMessage="No orders found for this customer."
                        />
                        {ordersPageInfo.totalPages > 1 && (
                            <Pagination
                                currentPage={filters.page || 1}
                                totalPages={ordersPageInfo.totalPages}
                                totalItems={ordersPageInfo.total}
                                itemsPerPage={ordersPageInfo.limit}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </div>
                </SectionCard>
            </div>

            {/* Block / Unblock Confirmation Modal */}
            <ConfirmationModal
                open={showBlockConfirm}
                onOpenChange={(open) => !open && setShowBlockConfirm(false)}
                onConfirm={handleBlockToggle}
                loading={blockLoading}
                type={isBlocked ? "success" : "danger"}
                title={isBlocked ? "Unblock Customer?" : "Block Customer?"}
                message={
                    isBlocked
                        ? `Are you sure you want to unblock ${customer.fullName}? They will regain full access to the platform.`
                        : `Are you sure you want to block ${customer.fullName}? They will lose access to the platform immediately.`
                }
                confirmText={isBlocked ? "Yes, Unblock" : "Yes, Block"}
                cancelText="Cancel"
                size="lg"
            />
        </Layout>
    );
};
